import React, { useState, useEffect } from 'react';
import { useSelected, useReadOnly } from 'slate-react';
import { icons, Button } from '@ublend-npm/aulaui-next';

import { BlockToolbar } from '../BlockToolbar/BlockToolbar';
import { ButtonWrapper, Container, LearnosityItem } from './InlineItem.styled';
import { useFetchInlineItem, useLearnositySdk } from './quizHooks';
import { StatusTypes } from '../../lib/loadingStatusTypes';
import { Learnosity } from './types';

export type InlineItemProp = {
  referenceId: string;
  onDeleteItem: () => void;
};

export const InlineItem = ({ referenceId, onDeleteItem }: InlineItemProp) => {
  const isSelected = useSelected();
  const isReadOnly = useReadOnly();
  const [isHovering, setHovering] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [submitText, setSubmitText] = useState('Submit answer');
  const [itemsApp, setItemsApp] = useState<Learnosity.SDK.ItemApp>();
  const [inlineQuizLoadingStatus, setInlineQuizLoadingStatus] = useState(
    StatusTypes.LOADING,
  );
  const learnositySDKLoaded = useLearnositySdk();
  const { state: fetchState } = useFetchInlineItem(
    referenceId,
    learnositySDKLoaded,
  );

  const hoverStateProps = {
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false),
  };

  useEffect(() => {
    if (fetchState.status === StatusTypes.SUCCESS) {
      if (!fetchState.data) {
        setInlineQuizLoadingStatus(StatusTypes.FAILED);
        return;
      }

      const { learnosityResponse } = fetchState.data;
      if (!learnosityResponse) {
        setInlineQuizLoadingStatus(StatusTypes.FAILED);
        return;
      }

      // Learnosity still needs request a few files on the background
      // to be able to inject the inline quiz. We will know it's done
      // when it calls the `readyListener`.
      setInlineQuizLoadingStatus(StatusTypes.LOADING);
      const { LearnosityItems } = window;
      const _itemsApp = LearnosityItems.init(learnosityResponse, {
        errorListener: () => {
          setInlineQuizLoadingStatus(StatusTypes.FAILED);
        },

        readyListener: () => {
          setInlineQuizLoadingStatus(StatusTypes.SUCCESS);
          setItemsApp(_itemsApp);
          const questions = _itemsApp.questions();

          Object.values(questions).forEach((question) => {
            question.on('changed', () => {
              setSubmitEnabled(true);
              setSubmitText('Submit answer');
            });
          });
        },
      });
    }

    if (fetchState.status === StatusTypes.FAILED) {
      setInlineQuizLoadingStatus(StatusTypes.FAILED);
    }
  }, [fetchState.status]);

  let fallbackMessage = '';
  switch (inlineQuizLoadingStatus) {
    case StatusTypes.SUCCESS:
      // Learnosity will override the content on success loading.
      fallbackMessage = '';
      break;
    case StatusTypes.FAILED:
      fallbackMessage = 'There was an error loading the quiz.';
      break;
    case StatusTypes.LOADING:
    case StatusTypes.IDLE:
      fallbackMessage = 'Loading...';
      break;
    default:
      break;
  }

  return (
    <Container isSelected={isSelected} {...hoverStateProps}>
      <LearnosityItem>
        <span className="learnosity-item" data-reference={referenceId}>
          {fallbackMessage}
        </span>

        {isReadOnly && inlineQuizLoadingStatus === StatusTypes.SUCCESS && (
          <ButtonWrapper>
            <Button
              disabled={!submitEnabled}
              type="secondary"
              size="small"
              onClick={() => {
                setSubmitText('Submitting');
                setSubmitEnabled(false);

                if (itemsApp) {
                  itemsApp.submit({
                    success: () => {
                      setSubmitText('Submitted');
                    },
                    error: () => {
                      setSubmitEnabled(true);
                      setSubmitText('Error');
                    },
                  });
                }
              }}
            >
              {submitText}
            </Button>
          </ButtonWrapper>
        )}
      </LearnosityItem>
      <BlockToolbar
        isVisible={isSelected || isHovering}
        items={[
          {
            title: 'Delete',
            isVisible:
              !isReadOnly && inlineQuizLoadingStatus !== StatusTypes.LOADING,
            icon: icons.Delete,
            onAction: onDeleteItem,
          },
        ]}
      />
    </Container>
  );
};
